/**
 * Language context: Spanish / English UI with path-based marketing locales.
 *
 * Marketing URLs use /es and /en. Product routes stay unprefixed; language
 * there follows this context (and localStorage). Do not infer from
 * navigator.language: Googlebot reports en-US and would otherwise overwrite
 * Spanish titles in the SERP.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import translations from '../i18n/translations';
import {
  DEFAULT_LOCALE,
  getLocaleFromPath,
  isAppPath,
  localizePath,
  swapLocaleInPath,
} from '../i18n/localePaths';

const STORAGE_KEY = 'clarcv_lang';
const LanguageContext = createContext(null);

const resolvePath = (obj, path) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const readStoredLang = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'es' || stored === 'en') return stored;
  } catch (_) {
    /* ignore */
  }
  return DEFAULT_LOCALE;
};

const detectInitialLang = () => {
  try {
    const fromPath = getLocaleFromPath(window.location.pathname);
    if (fromPath) return fromPath;
  } catch (_) {
    /* ignore */
  }
  return readStoredLang();
};

const applyApiLanguageHeaders = (lang) => {
  const code = lang === 'en' ? 'en' : 'es';
  axios.defaults.headers.common['Accept-Language'] = code;
  axios.defaults.headers.common['X-ClarCV-Lang'] = code;
};

/**
 * Must sit inside BrowserRouter so language switches can rewrite /es ↔ /en.
 */
export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fromPath = getLocaleFromPath(location.pathname);
    if (fromPath) {
      setLangState((prev) => (prev === fromPath ? prev : fromPath));
    }
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.lang = lang;
    applyApiLanguageHeaders(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (_) {
      /* ignore */
    }
  }, [lang]);

  const setLang = useCallback(
    (next) => {
      if (next !== 'es' && next !== 'en') {
        return;
      }
      setLangState(next);
      if (getLocaleFromPath(location.pathname)) {
        const nextPath = swapLocaleInPath(location.pathname, next);
        if (nextPath !== location.pathname) {
          navigate(`${nextPath}${location.search}${location.hash}`, { replace: true });
        }
      }
    },
    [location.pathname, location.search, location.hash, navigate]
  );

  const localePath = useCallback(
    (path) => localizePath(path, lang),
    [lang]
  );

  const t = useCallback(
    (key, vars) => {
      const table = translations[lang] || translations.es;
      let value = resolvePath(table, key);
      if (value == null) {
        value = resolvePath(translations.es, key);
      }
      if (typeof value !== 'string') {
        return key;
      }
      if (vars && typeof vars === 'object') {
        return value.replace(/\{(\w+)\}/g, (_, name) =>
          vars[name] != null ? String(vars[name]) : `{${name}}`
        );
      }
      return value;
    },
    [lang]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      localePath,
      t,
      isEs: lang === 'es',
      isEn: lang === 'en',
      isAppRoute: isAppPath(location.pathname),
    }),
    [lang, setLang, localePath, t, location.pathname]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
};

export default LanguageContext;
